import { DecoratorNode, LexicalNode, NodeKey, SerializedLexicalNode, Spread, $nodesOfType } from 'lexical';
import { File, Image, Code, X, Frame, FileImage, Video, Music, Type, FileJson, Box } from 'lucide-react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useState } from 'react';

export type PromptItemType = 'image' | 'document' | 'code' | 'element' | 'asset' | 'asset-image' | 'asset-video' | 'asset-audio' | 'asset-font' | 'asset-data' | 'asset-3d';

export interface PromptItemPayload {
  type: PromptItemType;
  label: string;
  id: string;
  metadata?: Record<string, any>;
  isTentative?: boolean;
}

export type SerializedPromptItemNode = Spread<
  {
    itemType: PromptItemType;
    label: string;
    id: string;
    metadata?: Record<string, any>;
    isTentative?: boolean;
  },
  SerializedLexicalNode
>;

export class PromptItemNode extends DecoratorNode<JSX.Element> {
  __itemType: PromptItemType;
  __label: string;
  __id: string;
  __metadata?: Record<string, any>;
  __isTentative: boolean;

  static getType(): string {
    return 'prompt-item';
  }

  static clone(node: PromptItemNode): PromptItemNode {
    return new PromptItemNode(
      node.__itemType,
      node.__label,
      node.__id,
      node.__metadata,
      node.__isTentative,
      node.__key
    );
  }

  constructor(
    type: PromptItemType,
    label: string,
    id: string,
    metadata?: Record<string, any>,
    isTentative: boolean = false,
    key?: NodeKey
  ) {
    super(key);
    this.__itemType = type;
    this.__label = label;
    this.__id = id;
    this.__metadata = metadata;
    this.__isTentative = isTentative;
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    dom.className = 'prompt-item-wrapper';
    dom.style.display = 'inline-block';
    dom.style.verticalAlign = 'middle';
    dom.style.margin = '0 2px';
    return dom;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedPromptItemNode): PromptItemNode {
    return $createPromptItemNode({
      type: serializedNode.itemType,
      label: serializedNode.label,
      id: serializedNode.id,
      metadata: serializedNode.metadata,
      isTentative: serializedNode.isTentative,
    });
  }

  exportJSON(): SerializedPromptItemNode {
    return {
      type: 'prompt-item',
      itemType: this.__itemType,
      label: this.__label,
      id: this.__id,
      metadata: this.__metadata,
      isTentative: this.__isTentative,
      version: 1,
    };
  }

  getTextContent(): string {
    // Return a marker that can be parsed and rendered in chat history
    // Format: __PROMPT_ITEM[type:element|id:edit-xxx|label:Game Title]__
    const escapedLabel = this.__label.replace(/\|/g, '\\|').replace(/\]/g, '\\]');
    return `__PROMPT_ITEM[type:${this.__itemType}|id:${this.__id}|label:${escapedLabel}]__`;
  }

  decorate(): JSX.Element {
    return <PromptItemComponent node={this} />;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  // Make the node atomic (deleted as one unit)
  isToken(): boolean {
    return true;
  }
}

function PromptItemComponent({ node }: { node: PromptItemNode }): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [isHovered, setIsHovered] = useState(false);
  const type = node.__itemType;
  const label = node.__label;
  const isTentative = node.__isTentative;

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.update(() => {
      node.remove();
    });
  };

  const getIcon = () => {
    switch (type) {
      case 'image':
        return <Image className="w-3 h-3" />;
      case 'document':
        return <File className="w-3 h-3" />;
      case 'code':
        return <Code className="w-3 h-3" />;
      case 'element':
        return <Frame className="w-3 h-3" />;
      case 'asset':
        return <FileImage className="w-3 h-3" />;
      case 'asset-image':
        return <Image className="w-3 h-3" />;
      case 'asset-video':
        return <Video className="w-3 h-3" />;
      case 'asset-audio':
        return <Music className="w-3 h-3" />;
      case 'asset-font':
        return <Type className="w-3 h-3" />;
      case 'asset-data':
        return <FileJson className="w-3 h-3" />;
      case 'asset-3d':
        return <Box className="w-3 h-3" />;
      default:
        return <File className="w-3 h-3" />;
    }
  };

  const getColorClasses = () => {
    if (isTentative) {
      // Tentative styling (faded gray) - same for all types
      return "bg-gray-100 dark:bg-gray-800/30 text-gray-400 dark:text-gray-500 border-gray-300 dark:border-gray-700 opacity-50";
    }

    // Permanent styling - different colors per type
    switch (type) {
      case 'image':
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50";
      case 'document':
        return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-900/50";
      case 'code':
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50";
      case 'element':
        return "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-300 dark:border-pink-700 hover:bg-pink-200 dark:hover:bg-pink-900/50";
      case 'asset':
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50";
      // Asset type-specific colors
      case 'asset-image':
        return "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700 hover:bg-sky-200 dark:hover:bg-sky-900/50";
      case 'asset-video':
        return "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700 hover:bg-violet-200 dark:hover:bg-violet-900/50";
      case 'asset-audio':
        return "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700 hover:bg-rose-200 dark:hover:bg-rose-900/50";
      case 'asset-font':
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 hover:bg-indigo-200 dark:hover:bg-indigo-900/50";
      case 'asset-data':
        return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-200 dark:hover:bg-emerald-900/50";
      case 'asset-3d':
        return "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 hover:bg-orange-200 dark:hover:bg-orange-900/50";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-900/50";
    }
  };

  const getRemoveButtonClasses = () => {
    // Remove button colors matching the type
    switch (type) {
      case 'image':
        return "bg-blue-200 dark:bg-blue-800 hover:bg-blue-300 dark:hover:bg-blue-700 border-blue-300 dark:border-blue-600";
      case 'document':
        return "bg-green-200 dark:bg-green-800 hover:bg-green-300 dark:hover:bg-green-700 border-green-300 dark:border-green-600";
      case 'code':
        return "bg-purple-200 dark:bg-purple-800 hover:bg-purple-300 dark:hover:bg-purple-700 border-purple-300 dark:border-purple-600";
      case 'element':
        return "bg-pink-200 dark:bg-pink-800 hover:bg-pink-300 dark:hover:bg-pink-700 border-pink-300 dark:border-pink-600";
      case 'asset':
        return "bg-amber-200 dark:bg-amber-800 hover:bg-amber-300 dark:hover:bg-amber-700 border-amber-300 dark:border-amber-600";
      // Asset type-specific colors
      case 'asset-image':
        return "bg-sky-200 dark:bg-sky-800 hover:bg-sky-300 dark:hover:bg-sky-700 border-sky-300 dark:border-sky-600";
      case 'asset-video':
        return "bg-violet-200 dark:bg-violet-800 hover:bg-violet-300 dark:hover:bg-violet-700 border-violet-300 dark:border-violet-600";
      case 'asset-audio':
        return "bg-rose-200 dark:bg-rose-800 hover:bg-rose-300 dark:hover:bg-rose-700 border-rose-300 dark:border-rose-600";
      case 'asset-font':
        return "bg-indigo-200 dark:bg-indigo-800 hover:bg-indigo-300 dark:hover:bg-indigo-700 border-indigo-300 dark:border-indigo-600";
      case 'asset-data':
        return "bg-emerald-200 dark:bg-emerald-800 hover:bg-emerald-300 dark:hover:bg-emerald-700 border-emerald-300 dark:border-emerald-600";
      case 'asset-3d':
        return "bg-orange-200 dark:bg-orange-800 hover:bg-orange-300 dark:hover:bg-orange-700 border-orange-300 dark:border-orange-600";
      default:
        return "bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600";
    }
  };

  return (
    <span
      className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border transition-colors ${getColorClasses()}`}
      contentEditable={false}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        userSelect: 'none',
        cursor: 'default',
      }}
    >
      {getIcon()}
      <span>{label}</span>
      {isHovered && !isTentative && (
        <button
          onClick={handleRemove}
          className={`absolute top-1/2 -translate-y-1/2 -right-1 rounded-full p-1 transition-colors border ${getRemoveButtonClasses()}`}
          aria-label="Remove item"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </span>
  );
}

export function $createPromptItemNode(payload: PromptItemPayload): PromptItemNode {
  return new PromptItemNode(
    payload.type,
    payload.label,
    payload.id,
    payload.metadata,
    payload.isTentative || false
  );
}

export function $isPromptItemNode(
  node: LexicalNode | null | undefined
): node is PromptItemNode {
  return node instanceof PromptItemNode;
}

// Helper function to find all tentative nodes
export function $getAllTentativeNodes(): PromptItemNode[] {
  const allPromptItems = $nodesOfType(PromptItemNode);
  return allPromptItems.filter(node => node.__isTentative);
}

// Helper function to convert all tentative nodes to permanent
export function $convertAllTentativeToPermanent(): void {
  const tentativeNodes = $getAllTentativeNodes();
  tentativeNodes.forEach(node => {
    const writable = node.getWritable();
    writable.__isTentative = false;
  });
}

// Helper function to remove all tentative nodes
export function $removeAllTentativeNodes(): void {
  const tentativeNodes = $getAllTentativeNodes();
  tentativeNodes.forEach(node => node.remove());
}

